import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ExternalLink, MessageCircle, Package, Truck } from "lucide-react";

const STEPS = [
  { key: "confirmed", label: "Confirmed", sub: "Payment received", icon: Check },
  { key: "production", label: "In production", sub: "Being made for you", icon: Package },
  { key: "shipped", label: "Shipped", sub: "On its way", icon: Truck },
];

const money = (c, cur) => `$${((c || 0) / 100).toFixed(2)} ${(cur || "usd").toUpperCase()}`;
const date = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

export const OrderStatusCard = ({ order, email }) => {
  const reached = { shipped: 3, in_production: 2, confirmed: 1, refunded: 0 }[order.status] ?? 1;
  const contactQs = new URLSearchParams({ email, subject: `Order #${order.order_ref} — ${order.status === "shipped" ? "not received" : "question"}`, ref: order.order_ref }).toString();
  return (
    <motion.div data-testid="order-status-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-[#ffffff] border border-[#e3e2dc] rounded-3xl p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a]">Order</p>
          <p data-testid="order-ref" className="font-display font-extrabold text-2xl mt-1">#{order.order_ref}</p>
        </div>
        <span data-testid="order-status-pill" className={`font-mono-d text-[9px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${order.status === "shipped" ? "bg-[#0a0a0b] text-white" : order.status === "refunded" ? "bg-[#f3f2ec] text-[#5f5e5a]" : "bg-[#ff2a85] text-white"}`}>
          {order.status === "confirmed" ? "Pre-order confirmed" : order.status === "in_production" ? "In production" : order.status}
        </span>
      </div>

      {order.status !== "refunded" && (
        <ol data-testid="order-timeline" className="mt-8 grid grid-cols-3 gap-2">
          {STEPS.map((s, i) => {
            const done = i < reached, Icon = s.icon;
            return (
              <li key={s.key} data-testid={`timeline-${s.key}`} className="relative">
                {i > 0 && <span className={`absolute top-4 -left-1/2 right-1/2 h-px ${done ? "bg-[#0a0a0b]" : "bg-[#e3e2dc]"}`} />}
                <div className="relative flex flex-col items-center text-center">
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center ${done ? "bg-[#0a0a0b] text-white" : "bg-[#f3f2ec] text-[#5f5e5a]"}`}><Icon size={14} /></span>
                  <span className={`mt-3 font-mono-d text-[9px] uppercase tracking-[0.2em] ${done ? "text-[#0a0a0b]" : "text-[#5f5e5a]"}`}>{s.label}</span>
                  <span className="text-[11px] text-[#5f5e5a] mt-0.5 hidden sm:block">{s.sub}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div className="mt-8 border-t border-[#e3e2dc] pt-5 space-y-2.5 text-sm">
        <Row label="Piece" value={order.product_name} />
        <Row label="Size" value={order.size} />
        {order.color && <Row label="Colour" value={order.color} />}
        {order.quantity > 1 && <Row label="Qty" value={String(order.quantity)} />}
        <Row label="Paid" value={money(order.amount_total, order.currency)} />
        <Row label="Ordered" value={date(order.paid_at)} />
        {order.production_at && <Row label="In production since" value={date(order.production_at)} />}
        {order.ship_to && <Row label="Ships to" value={order.ship_to} />}
      </div>

      {order.tracking ? (
        <div data-testid="order-tracking" className="mt-6 rounded-2xl bg-[#0a0a0b] text-white p-5">
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">Shipped {date(order.tracking.shipped_at)}</p>
          <p className="mt-2 text-sm">{order.tracking.carrier} · <span data-testid="tracking-number" className="font-mono-d tracking-wider">{order.tracking.number}</span></p>
          {order.tracking.url && (
            <a data-testid="tracking-link" href={order.tracking.url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#ff2a85] hover:bg-[#e60067] font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors">
              Track package <ExternalLink size={12} />
            </a>
          )}
        </div>
      ) : order.status !== "refunded" ? (
        <p data-testid="order-pending-note" className="mt-6 text-[#5f5e5a] text-sm leading-relaxed">
          {order.status === "in_production" ? "Your piece is being made right now. We'll email you the tracking number the moment it ships." : "Your pre-order is locked in. We'll update this page when your piece goes into production, and email tracking when it ships."}
        </p>
      ) : null}

      <Link data-testid="order-contact-link" to={`/contact?${contactQs}`} className="mt-6 inline-flex items-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a] hover:text-[#ff2a85] transition-colors">
        <MessageCircle size={13} /> {order.status === "shipped" ? "Haven't received it? Contact us" : "Question about this order? Contact us"}
      </Link>
    </motion.div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a]">{label}</span>
    <span className="font-medium text-right">{value || "—"}</span>
  </div>
);
